import type { ParamSpec, RecipientKind, RecipientTitle } from '../api/visitorsDesk'

/** Desk settings helpers. The server stays the authority; these only give inline messages. */

/** One term per line or comma separated; trimmed, blanks dropped, deduplicated case-insensitively. */
export function parseTerms(text: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of text.split(/[\n,]/)) {
    const t = part.trim()
    const k = t.toLowerCase()
    if (!t || seen.has(k)) continue
    seen.add(k)
    out.push(t)
  }
  return out
}

/** Mirrors settings_lists._HOST. */
const HOST = /^(?=.{1,255}$)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/

/** Mirrors settings_lists.normalise_domain without the check: lower-case; strip scheme, path, query, port, userinfo, leading www. */
export function normaliseDomain(raw: string): string {
  let s = (raw ?? '').trim().toLowerCase()
  s = s.replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
  s = s.split(/[/?#]/, 1)[0]
  s = s.slice(s.lastIndexOf('@') + 1)
  s = s.replace(/:\d*$/, '').replace(/^\.+|\.+$/g, '')
  if (s.startsWith('www.')) s = s.slice(4)
  return s
}

/** A message for a domain the server would refuse, else null. */
export function domainError(raw: string): string | null {
  if (!raw.trim()) return 'Enter a domain, like rcgroups.com'
  return HOST.test(normaliseDomain(raw)) ? null : 'That does not look like a domain, like rcgroups.com'
}

/** Mirrors alerts.settings._check_param's messages; `raw` is the input text, or a boolean for a switch. */
export function paramError(spec: ParamSpec, raw: string | boolean): string | null {
  if (spec.kind === 'bool') return typeof raw === 'boolean' ? null : `${spec.key} must be true or false`
  const text = typeof raw === 'string' ? raw.trim() : ''
  const n = text === '' ? NaN : Number(text)
  const lo = spec.lo ?? -Infinity
  const hi = spec.hi ?? Infinity
  const range = `${spec.lo} to ${spec.hi}`
  if (spec.kind === 'int') {
    return Number.isInteger(n) && n >= lo && n <= hi ? null : `${spec.key} must be a whole number from ${range}`
  }
  return Number.isFinite(n) && n >= lo && n <= hi ? null : `${spec.key} must be a number from ${range}`
}

/** Mirrors alerts.registry.rule_text: `{key}` and `{key:g}`; a number prints without a trailing .0. */
export function ruleTextPreview(template: string, params: Record<string, number | boolean>): string {
  return template.replace(/\{(\w+)(?::g)?\}/g, (whole, key: string) => (key in params ? String(params[key]) : whole))
}

/** One line under a title's recipient list. */
export function recipientsSummary(t: RecipientTitle): string {
  if (t.default) return `Every admin (${t.effective.length}) — the default`
  const n = t.effective.length
  return `${n} ${n === 1 ? 'person' : 'people'}`
}

export type RecipientChoice = { user_id: number; label: string; stale: boolean }

/** The checkbox rows under a title: every candidate, then each stored recipient who can't
 *  receive this any more (an editor removed from the title, an admin demoted), on its own row. */
export function recipientChoices(t: RecipientTitle, kind: RecipientKind): RecipientChoice[] {
  const ids = new Set(t.candidates.map((c) => c.user_id))
  return [
    ...t.candidates.map((c) => ({ user_id: c.user_id, label: kind === 'digest' ? `${c.email} (${c.role})` : c.email, stale: false })),
    ...t.user_ids.filter((id) => !ids.has(id))
      .map((id) => ({ user_id: id, label: `User #${id} — can't receive this any more`, stale: true })),
  ]
}

/** What starts ticked: the stored ids that are still candidates, so a stale one never blocks Save. */
export function initialPicked(t: RecipientTitle): number[] {
  const ids = new Set(t.candidates.map((c) => c.user_id))
  return t.user_ids.filter((id) => ids.has(id))
}
